import { useState, useEffect } from 'react';
import {
  Button, Badge, Input, Label,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
  Switch, Spinner, Separator,
} from '@rnd-payplus/alloy-react';
import { Plus, Copy, Pencil, Trash2, History, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentTenantId } from '../utils/auth';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { usePageShell } from '../contexts/PageShellContext';

interface FeatureFlag {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
}

interface Webhook {
  id: string;
  url: string;
  events: string[];
  enabled: boolean;
  created_at: string;
  last_delivery_status: string | null;
}

interface WebhookDelivery {
  id: string;
  event: string;
  status: string;
  response_code: number | null;
  attempts: number;
  created_at: string;
}

const WEBHOOK_EVENTS = ['dataflow.completed', 'dataflow.failed', 'connection.tested', 'connection.failed'];

const ConfigurationPage = () => {
  const [tab, setTab] = useState('flags');

  const tenantId = getCurrentTenantId();

  // ─── Feature Flags ───────────────────────────────────────────────────────────
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [flagsLoading, setFlagsLoading] = useState(false);

  const loadFlags = async () => {
    setFlagsLoading(true);
    try {
      const res = await fetch('/api/feature-flags');
      setFlags(await res.json());
    } finally {
      setFlagsLoading(false);
    }
  };

  const toggleFlag = async (flag: FeatureFlag) => {
    const newEnabled = !flag.enabled;
    setFlags(prev => prev.map(f => f.id === flag.id ? { ...f, enabled: newEnabled } : f));
    try {
      const res = await fetch(`/api/feature-flags/${flag.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newEnabled }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`${flag.name} ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch {
      setFlags(prev => prev.map(f => f.id === flag.id ? { ...f, enabled: flag.enabled } : f));
      toast.error('Failed to update flag');
    }
  };

  // ─── API Keys ────────────────────────────────────────────────────────────────
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [createKeyOpen, setCreateKeyOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [generating, setGenerating] = useState(false);
  const [revokeKeyOpen, setRevokeKeyOpen] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null);
  const [revokingKey, setRevokingKey] = useState(false);

  const loadApiKeys = async () => {
    if (!tenantId) return;
    try {
      const res = await fetch(`/api/api-keys?tenantId=${tenantId}`);
      if (!res.ok) {
        throw new Error(`Failed to load API keys (${res.status})`);
      }
      setApiKeys(await res.json());
    } catch (err) {
      setApiKeys([]);
      toast.error(err instanceof Error ? err.message : 'Failed to load API keys');
    }
  };

  const closeCreateKeyDialog = () => {
    setCreateKeyOpen(false);
    setNewKeyName('');
    setGeneratedKey('');
  };

  const generateKey = async () => {
    setGenerating(true);
    try {
      const res = await fetch('/api/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, name: newKeyName, scopes: [] }),
      });
      if (!res.ok) {
        throw new Error(`Failed to generate key (${res.status})`);
      }
      const data = await res.json();
      if (!data.fullKey) {
        throw new Error('Backend did not return the generated key');
      }
      setGeneratedKey(data.fullKey);
      loadApiKeys();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate key');
    } finally {
      setGenerating(false);
    }
  };

  const confirmRevokeKey = async () => {
    if (!keyToRevoke) return;
    setRevokingKey(true);
    try {
      const res = await fetch(`/api/api-keys/${keyToRevoke.id}?tenantId=${tenantId}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error(`Failed to revoke key (${res.status})`);
      }
      toast.success('API key revoked');
      setRevokeKeyOpen(false);
      setKeyToRevoke(null);
      loadApiKeys();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke key');
    } finally {
      setRevokingKey(false);
    }
  };

  // ─── Webhooks ────────────────────────────────────────────────────────────────
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [addWebhookOpen, setAddWebhookOpen] = useState(false);
  const [newWebhook, setNewWebhook] = useState({ url: '', events: [] as string[], enabled: true });
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [deliveryDrawerOpen, setDeliveryDrawerOpen] = useState(false);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [deleteWebhookOpen, setDeleteWebhookOpen] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState<Webhook | null>(null);
  const [deletingWebhook, setDeletingWebhook] = useState(false);

  const loadWebhooks = async () => {
    if (!tenantId) return;
    try {
      const res = await fetch(`/api/webhooks?tenantId=${tenantId}`);
      if (!res.ok) {
        throw new Error(`Failed to load webhooks (${res.status})`);
      }
      setWebhooks(await res.json());
    } catch (err) {
      setWebhooks([]);
      toast.error(err instanceof Error ? err.message : 'Failed to load webhooks');
    }
  };

  const closeWebhookDialog = () => {
    setAddWebhookOpen(false);
    setEditingWebhook(null);
    setNewWebhook({ url: '', events: [], enabled: true });
  };

  const handleEditWebhookOpen = (wh: Webhook) => {
    setEditingWebhook(wh);
    setNewWebhook({ url: wh.url, events: wh.events, enabled: wh.enabled });
    setAddWebhookOpen(true);
  };

  const saveWebhook = async () => {
    setSavingWebhook(true);
    try {
      const isEdit = !!editingWebhook;
      const url = isEdit ? `/api/webhooks/${editingWebhook.id}` : '/api/webhooks';
      const method = isEdit ? 'PUT' : 'POST';
      const body = isEdit
        ? { tenantId, url: newWebhook.url, events: newWebhook.events, enabled: newWebhook.enabled }
        : { tenantId, url: newWebhook.url, events: newWebhook.events };
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(`Failed to ${isEdit ? 'update' : 'create'} webhook (${res.status})`);
      }
      toast.success(isEdit ? 'Webhook updated' : 'Webhook created');
      closeWebhookDialog();
      loadWebhooks();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save webhook');
    } finally {
      setSavingWebhook(false);
    }
  };

  const confirmDeleteWebhook = async () => {
    if (!webhookToDelete) return;
    setDeletingWebhook(true);
    try {
      const res = await fetch(`/api/webhooks/${webhookToDelete.id}?tenantId=${tenantId}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error(`Failed to delete webhook (${res.status})`);
      }
      toast.success('Webhook deleted');
      setDeleteWebhookOpen(false);
      setWebhookToDelete(null);
      loadWebhooks();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete webhook');
    } finally {
      setDeletingWebhook(false);
    }
  };

  const openDeliveries = async (webhookId: string) => {
    setDeliveryDrawerOpen(true);
    const res = await fetch(`/api/webhook-deliveries?webhookId=${webhookId}`);
    setDeliveries(await res.json());
  };

  const toggleEventSelection = (ev: string) => {
    setNewWebhook(w => ({
      ...w,
      events: w.events.includes(ev)
        ? w.events.filter(e => e !== ev)
        : [...w.events, ev],
    }));
  };

  // Eager-load all data on mount for KPI cards
  useEffect(() => { loadFlags(); loadApiKeys(); loadWebhooks(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (tab === 'flags') loadFlags(); }, [tab]);
  useEffect(() => { if (tab === 'keys') loadApiKeys(); }, [tab, tenantId]);
  useEffect(() => { if (tab === 'webhooks') loadWebhooks(); }, [tab, tenantId]);

  const flagsEnabled = flags.filter(f => f.enabled).length;
  const totalApiKeys = apiKeys.length;
  const activeWebhooks = webhooks.filter(w => w.enabled).length;
  const totalWebhooks = webhooks.length;

  const getStatusVariant = (status: string): 'default' | 'destructive' | 'secondary' | 'outline' => {
    if (status === 'delivered' || status === 'Active') return 'default';
    if (status === 'failed') return 'destructive';
    return 'secondary';
  };

  usePageShell({
    title: 'Configuration Management',
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Configuration' },
    ],
  });

  return (
    <div>
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Flags Enabled" value={flagsEnabled} subtitle={`of ${flags.length}`} />
        <KPICard title="API Keys" value={totalApiKeys} subtitle="Active" />
        <KPICard title="Active Webhooks" value={activeWebhooks} subtitle="Enabled" />
        <KPICard title="Total Webhooks" value={totalWebhooks} subtitle="Configured" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full mb-6">
          <TabsTrigger className="flex-1" value="flags">Feature Flags</TabsTrigger>
          <TabsTrigger className="flex-1" value="keys">API Keys</TabsTrigger>
          <TabsTrigger className="flex-1" value="webhooks">Webhooks</TabsTrigger>
        </TabsList>

        {/* Feature Flags Tab */}
        <TabsContent value="flags">
              {flagsLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner className="h-6 w-6" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Feature</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Enabled</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flags.map(flag => (
                      <TableRow key={flag.id}>
                        <TableCell className="font-mono">{flag.name}</TableCell>
                        <TableCell>{flag.description}</TableCell>
                        <TableCell>
                          <Switch checked={flag.enabled} onCheckedChange={() => toggleFlag(flag)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="keys">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">API Keys</h2>
                <Button onClick={() => { setCreateKeyOpen(true); setGeneratedKey(''); setNewKeyName(''); }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Generate New Key
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Key Prefix</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last Used</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.map(key => (
                    <TableRow key={key.id}>
                      <TableCell>{key.name}</TableCell>
                      <TableCell className="font-mono">{key.key_prefix}*******</TableCell>
                      <TableCell>{new Date(key.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>{key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setKeyToRevoke(key);
                            setRevokeKeyOpen(true);
                          }}
                          title="Revoke key"
                          aria-label="Revoke API key"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {apiKeys.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        No API keys yet. Generate one to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Webhooks</h2>
                <Button onClick={() => setAddWebhookOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Webhook
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Delivery</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.map(wh => (
                    <TableRow key={wh.id}>
                      <TableCell className="max-w-[250px] truncate">
                        {wh.url}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {wh.events.map(e => <Badge key={e} variant="outline">{e}</Badge>)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={wh.enabled ? 'default' : 'secondary'}>
                          {wh.enabled ? 'Active' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {wh.last_delivery_status ? (
                          <Badge variant={getStatusVariant(wh.last_delivery_status)}>
                            {wh.last_delivery_status}
                          </Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeliveries(wh.id)}
                            title="Delivery history"
                            aria-label="View delivery history"
                          >
                            <History className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditWebhookOpen(wh)}
                            title="Edit webhook"
                            aria-label="Edit webhook"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setWebhookToDelete(wh);
                              setDeleteWebhookOpen(true);
                            }}
                            title="Delete webhook"
                            aria-label="Delete webhook"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
        </TabsContent>
      </Tabs>

      {/* Generate API Key Dialog */}
      <Dialog
        open={createKeyOpen}
        onOpenChange={(open) => { if (!open) closeCreateKeyDialog(); else setCreateKeyOpen(true); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generate API Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="key-name">Key Name</Label>
              <Input
                id="key-name"
                value={newKeyName}
                onChange={e => setNewKeyName(e.target.value)}
                placeholder="e.g. Production Key"
                className="mt-1"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Give this key a descriptive name (e.g. 'Production Key')
              </p>
            </div>
            {generatedKey && (
              <div className="rounded-md border border-yellow-300 bg-yellow-50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    Save this key now — it won't be shown again!
                  </span>
                </div>
                <div className="font-mono text-xs break-all bg-gray-100 p-2 rounded">
                  {generatedKey}
                </div>
                <Button variant="outline" size="sm" className="mt-2"
                  onClick={() => { navigator.clipboard.writeText(generatedKey); toast.success('Key copied!'); }}>
                  <Copy className="mr-2 h-3 w-3" />
                  Copy
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
            {!generatedKey && (
              <Button onClick={generateKey}
                disabled={!newKeyName || generating || !tenantId}>
                {generating ? <Spinner className="h-4 w-4" /> : 'Generate'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke API Key confirmation dialog */}
      <Dialog open={revokeKeyOpen} onOpenChange={setRevokeKeyOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Revoke API key</DialogTitle>
          </DialogHeader>
          {keyToRevoke && (
            <p className="py-2 text-sm">
              Are you sure you want to revoke{' '}
              <span className="font-medium">&ldquo;{keyToRevoke.name}&rdquo;</span>
              {' '}(<span className="font-mono">{keyToRevoke.key_prefix}*******</span>)?
              Any clients still using this key will immediately lose access. This action cannot be undone.
            </p>
          )}
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" disabled={revokingKey}>Cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={confirmRevokeKey} disabled={revokingKey}>
              {revokingKey ? 'Revoking...' : 'Revoke'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Webhook Dialog */}
      <Dialog
        open={addWebhookOpen}
        onOpenChange={(open) => { if (!open) closeWebhookDialog(); else setAddWebhookOpen(true); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingWebhook ? 'Edit Webhook' : 'Add Webhook'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="webhook-url">Endpoint URL</Label>
              <Input
                id="webhook-url"
                value={newWebhook.url}
                onChange={e => setNewWebhook(w => ({ ...w, url: e.target.value }))}
                placeholder="https://example.com/webhook"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Events</Label>
              <div className="mt-2 space-y-2">
                {WEBHOOK_EVENTS.map(ev => (
                  <label key={ev} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newWebhook.events.includes(ev)}
                      onChange={() => toggleEventSelection(ev)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">{ev}</span>
                  </label>
                ))}
              </div>
            </div>
            {editingWebhook && (
              <div className="flex items-center justify-between pt-2">
                <Label htmlFor="webhook-enabled" className="cursor-pointer">Enabled</Label>
                <Switch
                  id="webhook-enabled"
                  checked={newWebhook.enabled}
                  onCheckedChange={(checked) => setNewWebhook(w => ({ ...w, enabled: checked }))}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={savingWebhook}>Cancel</Button>
            </DialogClose>
            <Button
              onClick={saveWebhook}
              disabled={!newWebhook.url || newWebhook.events.length === 0 || !tenantId || savingWebhook}
            >
              {savingWebhook ? 'Saving...' : (editingWebhook ? 'Save Changes' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery History Dialog (replaces Drawer) */}
      <Dialog open={deliveryDrawerOpen} onOpenChange={setDeliveryDrawerOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Delivery History</DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveries.map(d => (
                <TableRow key={d.id}>
                  <TableCell className="text-xs">{d.event}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(d.status)}>
                      {d.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{d.response_code ?? '-'}</TableCell>
                  <TableCell className="text-xs">
                    {new Date(d.created_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
              {deliveries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No deliveries yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Webhook confirmation dialog */}
      <Dialog open={deleteWebhookOpen} onOpenChange={setDeleteWebhookOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete webhook</DialogTitle>
          </DialogHeader>
          {webhookToDelete && (
            <p className="py-2 text-sm">
              Are you sure you want to delete the webhook for{' '}
              <span className="font-medium break-all">{webhookToDelete.url}</span>?
              Any delivery history will also be removed. This action cannot be undone.
            </p>
          )}
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" disabled={deletingWebhook}>Cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={confirmDeleteWebhook} disabled={deletingWebhook}>
              {deletingWebhook ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfigurationPage;
